import { useState, useRef } from 'react'
import { Card } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { ArrowRight, ArrowLeft, CheckCircle2, ChevronLeft, ChevronRight } from 'lucide-react'
import bookStyles from '@/config/bookStyles.json'

// 添加类型定义
interface BookStyle {
  id: string
  name: string
  description: string
  image: string
  tags: string[]
}

interface BookStyles {
  styles: BookStyle[]
}

// 使用类型断言
const styles = (bookStyles as BookStyles).styles

interface TemplateSelectorProps {
  selectedTemplate: string | null
  onSelect: (template: string) => void
  onNext: () => void
  onPrevious: () => void
}

const TemplateSelector = ({ 
  selectedTemplate, 
  onSelect, 
  onNext, 
  onPrevious 
}: TemplateSelectorProps) => {
  const scrollRef = useRef<HTMLDivElement>(null)

  const handleScroll = (direction: 'left' | 'right') => {
    if (scrollRef.current) {
      const scrollAmount = 400 // 每次滚动的距离
      scrollRef.current.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth'
      })
    }
  }

  return (
    <div className="w-full">
      <Card className="p-8">
        <div className="max-w-3xl mx-auto">
          <h2 className="text-xl font-bold text-gray-800 mb-2">选择绘本风格</h2>
          <p className="text-sm text-gray-500 mb-8">
            为你的故事选择合适的绘本风格。
          </p>

          <div className="relative mb-8">
            <div className="absolute left-0 top-1/2 -translate-y-1/2 z-10">
              <Button
                variant="ghost"
                size="icon"
                className="rounded-full bg-white/80 shadow-md hover:bg-white"
                onClick={() => handleScroll('left')}
              >
                <ChevronLeft className="w-5 h-5" />
              </Button>
            </div>

            <div className="absolute right-0 top-1/2 -translate-y-1/2 z-10">
              <Button
                variant="ghost"
                size="icon"
                className="rounded-full bg-white/80 shadow-md hover:bg-white"
                onClick={() => handleScroll('right')}
              >
                <ChevronRight className="w-5 h-5" />
              </Button>
            </div>

            <div 
              ref={scrollRef}
              className="flex gap-6 overflow-x-auto scrollbar-hide px-2 py-4 -mx-2"
              style={{ scrollSnapType: 'x mandatory' }}
            >
              {styles.map((style: BookStyle) => (
                <div
                  key={style.id}
                  className={`flex-shrink-0 w-[300px] rounded-xl p-4 cursor-pointer transition-all
                    ${selectedTemplate === style.id 
                      ? 'bg-primary/5 ring-2 ring-primary/20 shadow-lg' 
                      : 'bg-white/50 hover:bg-primary/5 hover:shadow-md'
                    }`}
                  style={{ scrollSnapAlign: 'start' }}
                  onClick={() => onSelect(style.id)}
                >
                  <div className="aspect-video rounded-lg overflow-hidden mb-4 shadow-sm">
                    <img 
                      src={style.image} 
                      alt={style.name}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <h3 className="font-medium text-gray-800">{style.name}</h3>
                      {selectedTemplate === style.id && (
                        <CheckCircle2 className="w-5 h-5 text-primary" />
                      )}
                    </div>
                    
                    <p className="text-sm text-gray-500">
                      {style.description}
                    </p>

                    <div className="flex gap-2">
                      {style.tags.map((tag) => (
                        <span 
                          key={tag}
                          className="px-2 py-0.5 text-xs rounded-full bg-white/80 text-primary/70"
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="flex justify-between items-center">
            <Button
              variant="outline"
              onClick={onPrevious}
              className="flex items-center gap-2"
            >
              <ArrowLeft className="w-4 h-4" />
              <span>上一步</span>
            </Button>

            <Button
              onClick={onNext}
              disabled={!selectedTemplate}
              className="flex items-center gap-2 bg-gradient-to-r from-primary via-accent to-secondary hover:opacity-90"
            >
              <span>下一步</span>
              <ArrowRight className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </Card>
    </div>
  )
}

export default TemplateSelector 